import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { useState } from 'react'
import { SearchIcon } from 'assets/icons'
import { batchListUpload, getContent } from 'api/content'
import { addRelation, getRelations } from 'api/relations'
import Input from 'components/UI/Input'
import Sidebar from 'components/Sidebar'
import CreateCardPlaceholder from 'components/CreateCardPlaceholder'
import NetworkCard from 'components/NetworkCard'
import useCustomerSearch from 'hooks/api/useCustomerSearch'
import useRelationsContent from 'hooks/api/useRelationsContent'
import { t, wait } from 'helpers'
import { EventBus } from 'helpers/EventBus'
import { notif } from 'helpers/notif'
import { useAppStore, useProductionStore, useUserStore } from 'store'
import { PATHS, PROJECT_ACCESS, RELATIONS } from 'types/enums'
import { Content, NetworkCardType } from 'types/app'
import useContent from 'hooks/api/useContent'
import { projectSteps } from 'pages/Project/Project'
import { updateUser } from 'api/user'
import { getUser } from 'helpers/storage'
import { propertiesParser } from 'helpers/propertiesParser'

const GrantProjectAccessSidebar = (): JSX.Element => {
	const { productionId } = useParams()
	const [search, setSearch] = useState('')
	const [accessType, setOpenSidebar] = useProductionStore(
		useShallow((state) => [
			state.openGrantAccessSidebar,
			state.setOpenGrantAccessSidebar,
		]),
	)
	const [loading, setLoading] = useState(false)
	const { data: orgs } = useRelationsContent([RELATIONS.ORGANIZATION])
	const organization = orgs ? orgs[RELATIONS.ORGANIZATION][0] : undefined

	const { content: project } = useContent(productionId)

	const { data } = useCustomerSearch({
		fetch: !!accessType,
		organizationId: false,
		searchText: search,
		type: 'Creator,Company,ProjectOrganizer',
	})

	const grant = async (userId: string): Promise<void> => {
		const { user } = useUserStore.getState()
		try {
			await addRelation({
				targetId: userId,
				sourceId: productionId,
				relation: RELATIONS.INVITE,
				type: 'Content',
				status: accessType as string,
				query: `?emailTemplateId=GenericMessage&subject=${t(
					'APP_GRANT_PROJECT_ACCESS_SUBJECT',
				).replace(
					'[YourName]',
					user?.ContentTitle || '',
				)}&message=${encodeURIComponent(
					t('APP_GRANT_PROJECT_ACCESS_BODY')
						.replace(
							'[ProjectName]',
							project?.['Globalizationen-USContentTitle'] || '',
						)
						.replace(
							'[Link]',
							window.location.origin +
								`/${user?.CustomerRole?.toLowerCase()}/${
									PATHS.PROJECT
								}/${productionId}#${projectSteps.myproduction.key}-1`,
						),
				)}`,
			})

			await wait(1000)
			EventBus.$emit('refreshAccessListSidebar')
			setOpenSidebar(null)
		} catch (error) {
			console.log('error:', error)
		}
	}

	const sendMessage = async (
		message: string,
		user: NetworkCardType,
		orgId?: string,
	): Promise<void> => {
		try {
			setLoading(true)
			if (user) {
				const users = await batchListUpload(
					[user]?.map((user) => {
						return [
							{
								Name: 'Custom:User:Name',
								Value: user.name,
							},
							{
								Name: 'Custom:User:Email',
								Value: user.email,
							},
							{
								Name: 'Custom:User:PhoneNumber',
								Value: user.phone,
							},
							{
								Name: 'Customer:Location:Country',
								Value: (user.location as string) || '',
							},
							{
								Name: 'Customer:Location:City',
								Value: user.city || '',
							},
							{
								Name: 'Custom:User:Gender',
								Value: user.gender || '',
							},
							{
								Name: 'Custom:User:BirthDate',
								Value: user.birthday || '',
							},
							{
								Name: 'Customer:Role',
								Value: 'CastingDirector',
							},
						]
					}),
					orgId || '',
					message.replace(/^\s*\n/gm, '\\n \\n') || '',
					'CastingDirector',
					'ProjectOrganizer',
				)

				const customerId = users?.[0]?.customerId

				if (accessType === PROJECT_ACCESS.ASSISTANCE) {
					await addRelation({
						relation: RELATIONS.CREATOR,
						targetId: organization?.ContentId || '',
						sourceId: customerId,
						type: 'Customer',
					})

					const chats = await getRelations(
						getUser()?.['Custom:User:Id'] || '',
						'Customer',
						RELATIONS.CHAT,
					)
					const chatRelation = chats?.pageContent?.find(
						(rel) => rel.ownerId === getUser()?.['Custom:User:Id'],
					)

					if (chatRelation) {
						const chatContent = await getContent(chatRelation.targetId)
						const chat = propertiesParser<Content>(chatContent?.Properties)

						if (chat) {
							await addRelation({
								relation: RELATIONS.CHAT,
								targetId: chat.ContentId,
								sourceId: customerId,
								type: 'Customer',
							})

							await updateUser(customerId, [
								{
									Name: 'Customer:Chat:Room',
									Value: chat?.ContentChatArn,
								},
								{
									Name: 'Customer:Chat:Id',
									Value: chat.ContentId,
								},
								{
									Name: 'Customer:Pool:Default',
									Value: organization?.ContentId || '',
								},
							])
						}
					}
				}
				await grant(customerId)

				notif('success', t('APP_ADDED_USER'))
				setOpenSidebar(null)
			}
		} catch (error) {
			console.log('error:', error)
		} finally {
			setLoading(false)
			const { setOpenMessageSidebar } = useAppStore.getState()
			setOpenMessageSidebar(false, '', () => null, '')
		}
	}

	return (
		<Sidebar
			open={!!accessType}
			onClose={() => setOpenSidebar(null)}
			iconClose
			additional
			loading={loading}
			header={{
				title: 'APP_GRANT_USERS_SIDEBAR_TITLE',
				description: 'APP_GRANT_USERS_SIDEBAR_DESCRIPTION',
			}}>
			<>
				<Input
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder={t('APP_SEARCH')}
					variant="default"
					endAdornment={<SearchIcon color="var(--mono300)" />}
				/>
				<div className="grid2column">
					<CreateCardPlaceholder
						label="APP_ADD_CONTACT_LABEL"
						onClick={() => {
							const { setAddContactSidebar } = useAppStore.getState()
							setAddContactSidebar(
								true,
								(v) => {
									const { setOpenMessageSidebar, setAddContactSidebar } =
										useAppStore.getState()
									setOpenMessageSidebar(true, 'ADD_CONTACT', (message) =>
										sendMessage(
											message,
											v as NetworkCardType,
											organization?.ContentId,
										),
									)
									setAddContactSidebar(false, () => null)
								},
								true,
							)
						}}
					/>

					{data?.map((user) => (
						<NetworkCard
							data={user}
							key={user?.ContentId}
							onClick={() => grant(user?.ContentId)}
						/>
					))}
				</div>
			</>
		</Sidebar>
	)
}

export default GrantProjectAccessSidebar
